import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { Bold, Italic, List, Minus, Heading1, Heading2 } from 'lucide-react'
import {
    TooltipProvider,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
} from './ui/Tooltip'

export function RichTextEditor({
    value,
    onChange,
    placeholder = 'Start typing...',
    className = '',
    scrollToBottomTrigger = 0,
}) {
    const editorRef = useRef(null)
    const [isEmpty, setIsEmpty] = useState(true)
    const isInternalChange = useRef(false)

    useEffect(() => {
        if (isInternalChange.current) {
            isInternalChange.current = false
            return
        }
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value
            setIsEmpty(
                !value ||
                    value === '<br>' ||
                    value.replace(/<[^>]*>/g, '').trim() === ''
            )
        }
    }, [value])

    // Scroll to bottom when triggered externally (e.g. content added from AI chat)
    useEffect(() => {
        if (scrollToBottomTrigger > 0 && editorRef.current) {
            const el = editorRef.current
            el.scrollTop = el.scrollHeight
        }
    }, [scrollToBottomTrigger])

    const handleInput = useCallback(() => {
        if (!editorRef.current) return
        isInternalChange.current = true
        const html = editorRef.current.innerHTML
        const text = editorRef.current.innerText
        const empty = text.trim() === '' || html === '<br>'
        setIsEmpty(empty)
        onChange(empty ? '' : html, empty ? '' : text)
    }, [onChange])

    const execCommand = useCallback(
        (command, val) => {
            editorRef.current?.focus()
            document.execCommand(command, false, val)
            handleInput()
        },
        [handleInput]
    )

    const toolbarActions = useMemo(
        () => [
            {
                icon: Bold,
                label: 'Bold',
                command: () => execCommand('bold'),
            },
            {
                icon: Italic,
                label: 'Italic',
                command: () => execCommand('italic'),
            },
            {
                icon: Heading1,
                label: 'Heading',
                command: () => execCommand('formatBlock', 'h2'),
            },
            {
                icon: Heading2,
                label: 'Subheading',
                command: () => execCommand('formatBlock', 'h3'),
            },
            {
                icon: List,
                label: 'Bullet list',
                command: () => execCommand('insertUnorderedList'),
            },
            {
                icon: Minus,
                label: 'Divider',
                command: () => execCommand('insertHorizontalRule'),
            },
        ],
        [execCommand]
    )

    return (
        <div
            className={`tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 dark:tw-border-neutral-700/50 tw-bg-neutral-50 dark:tw-bg-neutral-800/40 focus-within:tw-border-neutral-400 dark:focus-within:tw-border-neutral-600 focus-within:tw-ring-1 focus-within:tw-ring-neutral-300/50 dark:focus-within:tw-ring-neutral-600/30 tw-transition-all ${className}`}
        >
            {/* Toolbar */}
            <TooltipProvider delayDuration={400}>
                <div className="tw-flex tw-items-center tw-gap-0.5 tw-px-2 tw-py-1.5 tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-b tw-border-solid tw-border-neutral-200 dark:tw-border-neutral-700/40 tw-bg-neutral-100/60 dark:tw-bg-neutral-800/30">
                    {toolbarActions.map(action => (
                        <Tooltip key={action.label}>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onMouseDown={e => {
                                        e.preventDefault()
                                        action.command()
                                    }}
                                    className="tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center tw-rounded tw-text-neutral-400 dark:tw-text-neutral-500 hover:tw-text-neutral-700 dark:hover:tw-text-neutral-300 hover:tw-bg-neutral-200/70 dark:hover:tw-bg-neutral-700/50 tw-transition-colors"
                                >
                                    <action.icon className="tw-w-3.5 tw-h-3.5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                {action.label}
                            </TooltipContent>
                        </Tooltip>
                    ))}
                </div>
            </TooltipProvider>

            {/* Editor — flex-1 fills remaining space, overflow scrolls */}
            <div className="tw-relative tw-flex-1 tw-min-h-0 tw-overflow-hidden">
                {isEmpty && (
                    <div
                        className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-px-3 tw-py-3 tw-text-sm tw-text-neutral-400 dark:tw-text-neutral-600 tw-pointer-events-none tw-select-none tw-leading-relaxed"
                        style={{ lineHeight: '1.625' }}
                    >
                        {placeholder}
                    </div>
                )}
                <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleInput}
                    className="
                        tw-min-h-[180px] tw-h-full tw-overflow-y-auto
                        tw-px-3 tw-py-3
                        tw-text-sm tw-text-neutral-800 dark:tw-text-neutral-200 tw-leading-relaxed
                        tw-outline-none
                        [&_h2]:tw-text-base [&_h2]:tw-font-semibold [&_h2]:tw-text-neutral-900 dark:[&_h2]:tw-text-neutral-100 [&_h2]:tw-mt-3 [&_h2]:tw-mb-1
                        [&_h3]:tw-text-sm [&_h3]:tw-font-semibold [&_h3]:tw-text-neutral-800 dark:[&_h3]:tw-text-neutral-200 [&_h3]:tw-mt-2 [&_h3]:tw-mb-1
                        [&_b]:tw-font-semibold [&_b]:tw-text-neutral-900 dark:[&_b]:tw-text-neutral-100
                        [&_strong]:tw-font-semibold [&_strong]:tw-text-neutral-900 dark:[&_strong]:tw-text-neutral-100
                        [&_i]:tw-italic [&_i]:tw-text-neutral-600 dark:[&_i]:tw-text-neutral-300
                        [&_em]:tw-italic [&_em]:tw-text-neutral-600 dark:[&_em]:tw-text-neutral-300
                        [&_ul]:tw-list-disc [&_ul]:tw-pl-5 [&_ul]:tw-my-1
                        [&_ol]:tw-list-decimal [&_ol]:tw-pl-5 [&_ol]:tw-my-1
                        [&_li]:tw-text-neutral-700 dark:[&_li]:tw-text-neutral-300 [&_li]:tw-mb-0.5
                        [&_hr]:tw-border-neutral-200 dark:[&_hr]:tw-border-neutral-700/50 [&_hr]:tw-my-3
                        [&_blockquote]:tw-border-l-2 [&_blockquote]:tw-border-solid [&_blockquote]:tw-border-neutral-300 dark:[&_blockquote]:tw-border-neutral-600 [&_blockquote]:tw-pl-3 [&_blockquote]:tw-my-2 [&_blockquote]:tw-italic [&_blockquote]:tw-text-neutral-600 dark:[&_blockquote]:tw-text-neutral-400
                    "
                />
            </div>
        </div>
    )
}
